// Medication -> body-system categories for questionnaire gating.
//
// Shannon (VIP clinical lead, 2026-08-12 session): "I always just look at
// medications first to understand whether a patient is healthy" — a
// blood-pressure medication opens the full cardiac question set, metformin
// opens the diabetes set, and a patient on nothing gets one umbrella
// question per system.
//
// The mapping is deliberately CONSERVATIVE and additive-only: an
// unrecognized medication triggers nothing (the umbrella question still
// catches the condition), and a false positive only ever ASKS MORE
// questions — it can never hide one.

/** The synthetic answer key umbrella-section gates match against. */
export const MEDICATION_SYSTEM_CATEGORIES_KEY = "medication_system_categories";

const CATEGORY_GENERICS: Record<string, string[]> = {
  cardiovascular: [
    "metoprolol", "atenolol", "carvedilol", "bisoprolol", "propranolol",
    "nebivolol", "labetalol", "lisinopril", "enalapril", "ramipril",
    "benazepril", "quinapril", "losartan", "valsartan", "olmesartan",
    "irbesartan", "telmisartan", "candesartan", "amlodipine", "nifedipine",
    "felodipine", "diltiazem", "verapamil", "hydrochlorothiazide",
    "chlorthalidone", "indapamide", "furosemide", "torsemide", "bumetanide",
    "spironolactone", "eplerenone", "amiodarone", "sotalol", "flecainide",
    "propafenone", "dronedarone", "digoxin", "isosorbide", "nitroglycerin",
    "ranolazine", "hydralazine", "clonidine", "sacubitril", "ivabradine",
    "atorvastatin", "rosuvastatin", "simvastatin", "pravastatin",
    "lovastatin", "ezetimibe", "fenofibrate", "gemfibrozil",
  ],
  bleeding_clotting: [
    "warfarin", "apixaban", "rivaroxaban", "dabigatran", "edoxaban",
    "clopidogrel", "prasugrel", "ticagrelor", "aspirin", "heparin",
    "enoxaparin", "fondaparinux", "dipyridamole", "cilostazol",
  ],
  endocrine_metabolic: [
    "metformin", "insulin", "glipizide", "glyburide", "glimepiride",
    "sitagliptin", "linagliptin", "saxagliptin", "empagliflozin",
    "dapagliflozin", "canagliflozin", "semaglutide", "liraglutide",
    "dulaglutide", "tirzepatide", "exenatide", "pioglitazone",
    "levothyroxine", "liothyronine", "methimazole", "propylthiouracil",
    "hydrocortisone", "prednisone", "fludrocortisone",
  ],
  respiratory: [
    "albuterol", "levalbuterol", "salmeterol", "formoterol", "fluticasone",
    "budesonide", "beclomethasone", "montelukast", "zafirlukast",
    "tiotropium", "ipratropium", "umeclidinium", "theophylline",
    "roflumilast", "benralizumab", "dupilumab",
  ],
  neurological: [
    "levetiracetam", "phenytoin", "carbamazepine", "oxcarbazepine",
    "lamotrigine", "valproate", "divalproex", "topiramate", "zonisamide",
    "lacosamide", "gabapentin", "pregabalin", "sumatriptan", "rizatriptan",
    "erenumab", "donepezil", "memantine", "rivastigmine", "ropinirole",
    "pramipexole", "levodopa", "carbidopa", "riluzole", "tetrabenazine",
  ],
  gastrointestinal: [
    "omeprazole", "esomeprazole", "pantoprazole", "lansoprazole",
    "rabeprazole", "famotidine", "sucralfate", "mesalamine", "sulfasalazine",
    "budesonide", "ondansetron", "metoclopramide", "linaclotide",
    "lubiprostone", "dicyclomine",
  ],
  hepatic: [
    "ursodiol", "lactulose", "rifaximin", "entecavir", "sofosbuvir",
  ],
  renal: [
    "sevelamer", "cinacalcet", "calcitriol", "epoetin", "darbepoetin",
    "patiromer", "sodium zirconium",
  ],
  psychiatric: [
    "sertraline", "fluoxetine", "escitalopram", "citalopram", "paroxetine",
    "venlafaxine", "desvenlafaxine", "duloxetine", "bupropion",
    "mirtazapine", "trazodone", "vilazodone", "vortioxetine", "lithium",
    "quetiapine", "olanzapine", "risperidone", "aripiprazole",
    "ziprasidone", "lurasidone", "paliperidone", "clozapine", "haloperidol",
    "alprazolam", "lorazepam", "clonazepam", "diazepam", "temazepam",
    "buspirone", "amphetamine", "dextroamphetamine", "lisdexamfetamine",
    "methylphenidate", "atomoxetine", "guanfacine", "naltrexone",
    "buprenorphine", "methadone", "acamprosate", "disulfiram", "zolpidem",
    "eszopiclone", "suvorexant",
  ],
  musculoskeletal_pain: [
    "cyclobenzaprine", "methocarbamol", "baclofen", "tizanidine",
    "carisoprodol", "meloxicam", "ibuprofen", "naproxen", "celecoxib",
    "diclofenac", "indomethacin", "oxycodone", "hydrocodone", "tramadol",
    "morphine", "hydromorphone", "fentanyl", "tapentadol", "codeine",
    "methotrexate", "hydroxychloroquine", "leflunomide", "adalimumab",
    "etanercept", "infliximab", "tofacitinib", "allopurinol", "febuxostat",
    "colchicine", "alendronate", "risedronate", "zoledronic", "denosumab",
    "teriparatide",
  ],
  cancer: [
    "tamoxifen", "anastrozole", "letrozole", "exemestane", "imatinib",
    "capecitabine", "abiraterone", "enzalutamide", "bicalutamide",
    "leuprolide", "palbociclib", "lenalidomide", "rituximab",
  ],
  infectious: [
    "tenofovir", "emtricitabine", "bictegravir", "dolutegravir",
    "efavirenz", "darunavir", "ritonavir", "valacyclovir", "isoniazid",
    "rifampin", "dapsone",
  ],
  ob_gyn: [
    "estradiol", "conjugated estrogens", "progesterone", "norethindrone",
    "medroxyprogesterone", "ethinyl estradiol", "levonorgestrel",
    "drospirenone", "norgestimate", "clomiphene", "raloxifene",
  ],
};

/** generic (lowercase) -> categories it triggers; built once at module load. */
const GENERIC_TO_CATEGORIES: Map<string, string[]> = (() => {
  const map = new Map<string, string[]>();
  for (const [category, generics] of Object.entries(CATEGORY_GENERICS)) {
    for (const generic of generics) {
      const existing = map.get(generic) ?? [];
      existing.push(category);
      map.set(generic, existing);
    }
  }
  return map;
})();

/** Categories triggered by a blob of medication-bearing text. Substring
 *  matching on normalized text: "Metoprolol Succinate ER 50 mg" matches
 *  "metoprolol"; unmatched text contributes nothing. */
export function deriveMedicationSystemCategories(texts: string[]): string[] {
  const haystack = ` ${texts.join(" ").toLowerCase()} `;
  const categories = new Set<string>();

  for (const [generic, cats] of GENERIC_TO_CATEGORIES) {
    if (haystack.includes(generic)) {
      for (const category of cats) {
        categories.add(category);
      }
    }
  }

  return [...categories].sort();
}

/** Derive the categories from a questionnaire answers record. Every string
 *  value (and string array member) contributes EXCEPT answers to allergy
 *  questions — "allergic to aspirin" must not open the bleeding section. */
export function deriveMedicationSystemCategoriesFromAnswers(
  answers: Record<string, unknown>,
): string[] {
  const texts: string[] = [];

  for (const [key, value] of Object.entries(answers)) {
    if (/allerg/u.test(key)) {
      continue;
    }

    if (typeof value === "string") {
      texts.push(value);
    } else if (Array.isArray(value)) {
      for (const item of value) {
        if (typeof item === "string") {
          texts.push(item);
        }
      }
    }
  }

  return deriveMedicationSystemCategories(texts);
}


// ---------------------------------------------------------------------------
// Medication-implied conditions for the chart's review of systems.
//
// Shannon (2026-09-01): "if I say lisinopril, in its systems review on the
// history and physical, it should say hypertension. If I'm on cholesterol
// medications, it should say hyperlipidemia... someone's on lithium, someone's
// on Ativan — you're able to say they have a positive history of psychiatric
// issues." Conservative: only unambiguous drug -> condition pairs; a match adds
// a labelled finding ("Hypertension (on lisinopril)") that a clinician can
// still edit, and an unrecognized medication adds nothing.
// ---------------------------------------------------------------------------

export interface MedicationImpliedCondition {
  generic: string;
  system:
    | "cardiovascular"
    | "pulmonary"
    | "renal"
    | "hepatic"
    | "neurological"
    | "gastrointestinal"
    | "endocrine"
    | "hematological"
    | "psychiatric"
    | "musculoskeletal";
  condition: string;
}

const IMPLIED: Array<[string[], MedicationImpliedCondition["system"], string]> = [
  [["lisinopril", "enalapril", "ramipril", "benazepril", "quinapril", "losartan", "valsartan", "olmesartan", "irbesartan", "telmisartan", "candesartan", "amlodipine", "nifedipine", "felodipine", "hydrochlorothiazide", "chlorthalidone", "indapamide", "clonidine", "hydralazine"], "cardiovascular", "Hypertension"],
  [["metoprolol", "atenolol", "carvedilol", "bisoprolol", "propranolol", "nebivolol", "labetalol"], "cardiovascular", "Hypertension or cardiac condition (beta blocker)"],
  [["atorvastatin", "rosuvastatin", "simvastatin", "pravastatin", "lovastatin", "ezetimibe", "fenofibrate", "gemfibrozil"], "cardiovascular", "Hyperlipidemia"],
  [["amiodarone", "sotalol", "flecainide", "propafenone", "dronedarone", "digoxin"], "cardiovascular", "Abnormal heart rhythm"],
  [["isosorbide", "nitroglycerin", "ranolazine"], "cardiovascular", "Angina / coronary disease"],
  [["furosemide", "torsemide", "bumetanide", "spironolactone", "eplerenone", "sacubitril", "ivabradine"], "cardiovascular", "Heart failure or fluid overload (diuretic)"],
  [["warfarin", "apixaban", "rivaroxaban", "dabigatran", "edoxaban", "enoxaparin"], "hematological", "Anticoagulated"],
  [["clopidogrel", "prasugrel", "ticagrelor"], "cardiovascular", "Antiplatelet therapy"],
  [["metformin", "insulin", "glipizide", "glyburide", "glimepiride", "sitagliptin", "linagliptin", "empagliflozin", "dapagliflozin", "canagliflozin", "pioglitazone"], "endocrine", "Diabetes"],
  [["semaglutide", "liraglutide", "dulaglutide", "tirzepatide", "exenatide"], "endocrine", "Diabetes or weight management (GLP-1)"],
  [["levothyroxine", "liothyronine"], "endocrine", "Hypothyroidism"],
  [["methimazole", "propylthiouracil"], "endocrine", "Hyperthyroidism"],
  [["albuterol", "levalbuterol", "salmeterol", "formoterol", "fluticasone", "budesonide", "montelukast", "tiotropium", "ipratropium", "umeclidinium"], "pulmonary", "Asthma or COPD (inhaler)"],
  [["omeprazole", "esomeprazole", "pantoprazole", "lansoprazole", "rabeprazole", "famotidine"], "gastrointestinal", "Acid reflux / GERD"],
  [["levetiracetam", "phenytoin", "carbamazepine", "oxcarbazepine", "lamotrigine", "valproate", "divalproex", "topiramate", "zonisamide", "lacosamide"], "neurological", "Seizure disorder (anticonvulsant)"],
  [["sumatriptan", "rizatriptan", "erenumab"], "neurological", "Migraine"],
  [["donepezil", "memantine", "rivastigmine"], "neurological", "Dementia"],
  [["ropinirole", "pramipexole", "levodopa", "carbidopa"], "neurological", "Parkinson's disease"],
  [["gabapentin", "pregabalin"], "neurological", "Neuropathic pain"],
  [["lithium"], "psychiatric", "Bipolar / mood disorder"],
  [["lorazepam", "alprazolam", "clonazepam", "diazepam", "buspirone"], "psychiatric", "Anxiety"],
  [["sertraline", "fluoxetine", "citalopram", "escitalopram", "paroxetine", "venlafaxine", "duloxetine", "bupropion", "mirtazapine", "trazodone"], "psychiatric", "Depression or anxiety"],
  [["quetiapine", "olanzapine", "risperidone", "aripiprazole", "ziprasidone", "haloperidol"], "psychiatric", "Psychiatric condition (antipsychotic)"],
  [["allopurinol", "febuxostat", "colchicine"], "musculoskeletal", "Gout"],
  [["methotrexate", "hydroxychloroquine", "adalimumab", "etanercept"], "musculoskeletal", "Rheumatoid or autoimmune arthritis"],
  [["oxycodone", "hydrocodone", "morphine", "tramadol", "fentanyl"], "musculoskeletal", "Chronic pain (opioid)"],
  [["sevelamer", "calcitriol", "epoetin"], "renal", "Chronic kidney disease"],
  [["ursodiol", "lactulose", "rifaximin"], "hepatic", "Liver disease"],
];

const IMPLIED_BY_GENERIC: Map<string, MedicationImpliedCondition> = new Map(
  IMPLIED.flatMap(([generics, system, condition]) =>
    generics.map((generic) => [generic, { generic, system, condition }] as const),
  ),
);

/** Conditions implied by medication names/text (substring match on the
 *  normalized text, longest generic first so "levothyroxine" never falls to a
 *  shorter neighbour). */
export function deriveMedicationImpliedConditions(texts: string[]): MedicationImpliedCondition[] {
  const haystack = ` ${texts.join(" ").toLowerCase()} `;
  const out: MedicationImpliedCondition[] = [];
  const seen = new Set<string>();

  for (const [generic, implied] of [...IMPLIED_BY_GENERIC.entries()].sort(
    (a, b) => b[0].length - a[0].length,
  )) {
    if (haystack.includes(generic) && !seen.has(generic)) {
      seen.add(generic);
      out.push(implied);
    }
  }

  return out;
}

/** Findings implied by free-text surgical history (Shannon: "if I've had a
 *  pacemaker, it should say abnormal rhythm pacemaker... under cardiovascular";
 *  artificial joints "we will know by their surgical history"). */
export function deriveSurgicalHistoryImpliedFindings(
  texts: string[],
): Array<{ system: MedicationImpliedCondition["system"] | "obgyn"; label: string; code: string }> {
  const haystack = ` ${texts.join(" ").toLowerCase()} `;
  const rules: Array<[RegExp, MedicationImpliedCondition["system"] | "obgyn", string, string]> = [
    [/pacemaker/u, "cardiovascular", "Pacemaker (per surgical history)", "surgical:pacemaker"],
    [/\b(?:aicd|icd|defibrillator)\b/u, "cardiovascular", "AICD / defibrillator (per surgical history)", "surgical:aicd"],
    [/\bstent/u, "cardiovascular", "Cardiac stent (per surgical history)", "surgical:stent"],
    [/\b(?:bypass|cabg)\b/u, "cardiovascular", "Bypass surgery (per surgical history)", "surgical:bypass"],
    [/\b(?:hip|knee|shoulder|ankle|elbow)\b[^.;]{0,25}\b(?:replacement|arthroplasty)|\btotal (?:hip|knee|shoulder)\b|\bjoint replacement/u, "musculoskeletal", "Artificial joint (per surgical history)", "surgical:artificial-joint"],
    [/(?:spinal cord|nerve) stimulator/u, "musculoskeletal", "Nerve stimulator (per surgical history)", "surgical:nerve-stimulator"],
    [/\b(?:plates?|screws?|rods?|pins?)\b/u, "musculoskeletal", "Metal implant (per surgical history)", "surgical:metal-implant"],
    [/hysterectomy/u, "obgyn", "Hysterectomy (per surgical history)", "surgical:hysterectomy"],
  ];

  return rules
    .filter(([pattern]) => pattern.test(haystack))
    .map(([, system, label, code]) => ({ system, label, code }));
}
